import { useState } from "react";
import { toast } from "sonner";
import { Upload, FileSpreadsheet } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { GlassCard, GradButton, Field } from "@/components/erp";
import { SelectBox } from "@/components/Inputs";
import api, { errMsg } from "@/lib/api";

const ALANLAR = [["tarih", "Tarih *"], ["aciklama", "Açıklama"], ["tutar", "Tutar (+ giriş / − çıkış)"], ["borc", "Borç (çıkan)"], ["alacak", "Alacak (giren)"], ["bakiye", "Bakiye"]];
export const EKSIK_OPS = [{ value: "goster", label: "Farkları göster" }, { value: "goster_ekle", label: "Farkları göster ve tek tıkla hesap hareketi oluşturabileyim" }];

const Onizleme = ({ p }) => (
  <div className="overflow-x-auto rounded-xl border border-white/5 mt-4" data-testid="mtb-onizleme">
    <table className="w-full text-xs">
      <thead><tr>{p.kolonlar.map((k, i) => <th key={i} className="p-2 text-left text-muted-foreground font-medium whitespace-nowrap">{k}</th>)}</tr></thead>
      <tbody>{p.satirlar.slice(0, 6).map((r, i) => <tr key={i} className="border-t border-white/5">{r.map((c, j) => <td key={j} className="p-2 whitespace-nowrap max-w-[220px] truncate">{String(c)}</td>)}</tr>)}</tbody>
    </table>
  </div>
);

export const MutabakatYukle = ({ hesaplar, onDone, onCancel }) => {
  const [hesap, setHesap] = useState(hesaplar[0]?.id || "");
  const [p, setP] = useState(null);
  const [kol, setKol] = useState({});
  const [opt, setOpt] = useState({ tolerans_gun: 3, ters_isaret: false, eksik_goster: "" });
  const [busy, setBusy] = useState(false);
  const yukle = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("dosya", file);
    setBusy(true);
    try {
      const { data } = await api.post("/mutabakat/onizle", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setP(data); setKol(data.tahmin);
      toast.success(`${data.satirlar.length} satır okundu — sütunları kontrol edin`);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const baslat = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/mutabakatlar", { hesap_id: hesap, dosya_adi: p.dosya_adi, kolonlar: kol, satirlar: p.satirlar, ...opt, tolerans_gun: Number(opt.tolerans_gun) });
      toast.success(`${data.no} · ${data.ozet.eslesen}/${data.ozet.ekstre_satir} satır eşleşti`);
      onDone(data);
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  const ops = [{ value: "-", label: "—" }, ...(p?.kolonlar || []).map((k, i) => ({ value: String(i), label: k }))];
  return (
    <GlassCard className="p-6 mb-6 space-y-5" data-testid="mtb-yukle">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Hesap *"><SelectBox testid="mtb-hesap" value={hesap} onChange={setHesap} options={hesaplar.map((h) => ({ value: h.id, label: `${h.ad} · ${h.para_birimi}` }))} /></Field>
        <Field label="Banka ekstresi (Excel, CSV veya PDF · en fazla 10 MB)">
          <label className="flex items-center gap-2 h-10 px-3 rounded-md bg-white/5 border border-white/10 text-sm cursor-pointer hover:bg-white/10" data-testid="mtb-dosya-label">
            <Upload className="w-4 h-4" /><span className="truncate">{busy && !p ? "Okunuyor…" : p?.dosya_adi || "Dosya seçin"}</span>
            <input type="file" accept=".xlsx,.xls,.csv,.txt,.pdf" className="hidden" data-testid="mtb-dosya" onChange={(e) => { yukle(e.target.files?.[0]); e.target.value = ""; }} />
          </label>
        </Field>
      </div>
      {p && <>
        <div><div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3 flex items-center gap-2"><FileSpreadsheet className="w-4 h-4" />Sütun eşleştirme</div>
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">{ALANLAR.map(([k, l]) => (
            <Field key={k} label={l}><SelectBox testid={`mtb-kolon-${k}`} value={kol[k] === undefined || kol[k] === null ? "-" : String(kol[k])} onChange={(v) => setKol((s) => ({ ...s, [k]: v === "-" ? null : Number(v) }))} options={ops} /></Field>
          ))}</div>
          <p className="text-[11px] text-muted-foreground mt-2">Tek bir işaretli tutar sütunu ya da ayrı borç / alacak sütunları seçin. Tutar seçiliyse borç / alacak dikkate alınmaz.</p>
          <Onizleme p={p} />
        </div>
        <div className="grid sm:grid-cols-3 gap-4 items-end">
          <Field label="Tarih toleransı (gün, 0–15)"><Input data-testid="mtb-tolerans" type="number" min="0" max="15" className="bg-white/5" value={opt.tolerans_gun} onChange={(e) => setOpt((s) => ({ ...s, tolerans_gun: e.target.value }))} /></Field>
          <Field label="Ekstrede olup sistemde olmayan kalemler (ör. banka masrafı)">
            <div className="flex items-center gap-2"><div className="flex-1"><SelectBox testid="mtb-eksik" placeholder="Boş bırakırsanız gösterilmez" value={opt.eksik_goster} onChange={(v) => setOpt((s) => ({ ...s, eksik_goster: v }))} options={EKSIK_OPS} /></div>
              {opt.eksik_goster && <button className="text-xs text-muted-foreground hover:text-white" data-testid="mtb-eksik-temizle" onClick={() => setOpt((s) => ({ ...s, eksik_goster: "" }))}>temizle</button>}</div>
          </Field>
          <label className="flex items-center gap-2 text-sm pb-2"><Switch data-testid="mtb-ters" checked={opt.ters_isaret} onCheckedChange={(v) => setOpt((s) => ({ ...s, ters_isaret: v }))} />Tutar işaretini ters çevir</label>
        </div>
      </>}
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="px-5 py-2 rounded-full border border-white/10 text-sm" data-testid="mtb-vazgec">Vazgeç</button>
        <GradButton onClick={baslat} disabled={busy || !p || !hesap} data-testid="mtb-baslat">Mutabakatı başlat</GradButton>
      </div>
    </GlassCard>
  );
};
